/*
 * Action card (design/50-components.md "Card"). The one card variant a shadow is allowed on,
 * because the 1px to 4px step is its lift feedback rather than decoration.
 */
.root {
  display: flex;
  /*
   * Centred, not top-aligned. The text block is shorter than the 40px chip with a one-line preview
   * and taller with two, so a top-aligned row leaves the chip or the arrow riding high on one of
   * the two. The prototype card was top-aligned and it only showed once the two were side by side.
   */
  align-items: center;
  gap: var(--space__md);
  padding: var(--space__lg);
  /*
   * A touch more breathing room before the icon chip than the other three edges. 20px is
   * deliberately off the space scale, between `lg` (16px) and `xl` (24px): a design call from Janne
   * on the shipped dashboard card, where `lg` read tight and `xl` loose. It moves here with the
   * component rather than being rounded onto the scale on the way.
   */
  padding-inline-start: 20px;
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__xl);
  background: var(--color__white);
  box-shadow: 0 1px 2px oklch(from var(--color__gray-900) l c h / 0.15);
  text-align: start;
  cursor: pointer;
  /* 150ms, the moving-affordance duration (design/10-foundations.md "Motion"). */
  transition:
    box-shadow 150ms,
    border-color 150ms,
    background-color 150ms;
}

/*
 * Hover is the surface responding, and nothing else: a darker border and the lift. The ground
 * used to go `lilac-50` as well, and that is the one thing removed — lilac is the selected fill,
 * so a lilac ground on a card you are merely pointing at says "selected" about something that is
 * not (DESIGN.md rule 26, a state treatment is not an emphasis treatment). PLT-6705.
 */
.root:hover {
  border-color: var(--color__gray-400);
  box-shadow: 0 4px 16px oklch(from var(--color__gray-900) l c h / 0.15);
}

/*
 * Focus is the stroke alone. This card has its own border, so the border *is* the focus line,
 * thickened to 2px by an inset shadow rather than by a wider border, which would grow the box and
 * shift the row. No added ring and no lift: the card used to take the whole hover treatment plus
 * the ring on top, which said two things where one will do (PLT-6705).
 */
.root:focus-visible {
  outline: none;
  border-color: var(--focus-ring__color);
  box-shadow: inset 0 0 0 1px var(--focus-ring__color);
}

/* 40 by 40, `lilac-100` ground, `lilac-500` glyph, `lg` radius. One height for the row. */
.chip {
  flex: 0 0 auto;
  inline-size: 40px;
  block-size: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__lg);
  background: var(--color__lilac-100);
  color: var(--color__lilac-500);
}

/*
 * `sm` between the title and its preview: the card's inset is `lg`, so one step down for the group
 * and one more for two rows of one group. `xs` is the icon-to-text step at the bottom of the depth
 * ladder and leaves the title closer to the preview than the preview's own leading, which is how
 * the shipped card had it.
 *
 * `min-inline-size: 0` is what lets the title actually ellipsis-truncate inside a flex row.
 */
.text {
  min-inline-size: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
}

/*
 * The preview clamps at two lines: the one clamp the design system specifies rather than tolerates
 * (design/50-components.md "Truncation", DESIGN.md rule 27), and this file holds the allowlist entry
 * for it in `css-value-rules.spec.ts`. `-webkit-box` is the only spelling every browser honours; the
 * whole card's `title` carries the full sentence.
 *
 * The clamp needs `overflow: hidden`, and that is why the text-box trim is switched off here: `Text`
 * trims every line box to cap height and baseline, so with the overflow clipped the ascender of an
 * "f" and the tail of a "g" are cut off, and "figures" renders as "tigures". The untrimmed box keeps
 * the whole glyph inside the clip, at the cost of the half-leading the trim removes, so the
 * title-to-preview gap reads a step wider than the `sm` above declares: between `sm` and the `md`
 * the card spec asks for. The compound selector is what lets this outrank `Text`'s own `.root` rule.
 */
.text .preview {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-box-trim: none;
  text-box-edge: auto;
}

/*
 * An icon, so the stroke takes `color` and the box is the icon's own. `flex` so it centres on the
 * row rather than sitting on the text baseline an inline SVG inherits. 150ms on both properties: the
 * shift is a moving affordance, and an instant jump is the tell that a card was styled rather than
 * built.
 */
.arrow {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  margin-inline-start: auto;
  color: var(--color__gray-400);
  transition:
    color 150ms,
    transform 150ms;
}

.root:hover .arrow,
.root:focus-visible .arrow {
  transform: translateX(2px);
  color: var(--color__lilac-500);
}
